import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import SettingsIcon from '@material-ui/icons/Settings'
import SyncIcon from '@material-ui/icons/Sync'
import axios from 'axios'
import useAggregateVendors from './useAggregateVendors'

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3)
  },
  statsRow: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap'
  },
  statCard: {
    padding: theme.spacing(2, 3),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    minWidth: 120,
    textAlign: 'center'
  },
  statValue: {
    fontWeight: 700,
    fontSize: '1.75rem',
    color: theme.palette.primary.main
  },
  statLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  tableHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2)
  },
  activeChip: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    fontWeight: 600
  },
  inactiveChip: {
    backgroundColor: '#fafafa',
    color: theme.palette.text.secondary
  },
  emptyState: {
    padding: theme.spacing(6),
    textAlign: 'center'
  },
  wvrCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1)
  },
  wvrLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5)
  },
  wvrRight: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap'
  },
  wvrTimestamp: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem'
  },
  chipCreated: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    fontWeight: 600
  },
  chipUpdated: {
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    fontWeight: 600
  },
  chipUnchanged: {
    backgroundColor: '#fafafa',
    color: theme.palette.text.secondary
  },
  chipErrors: {
    backgroundColor: '#fbe9e7',
    color: '#bf360c',
    fontWeight: 600
  }
}))

export default function SuppliersTab () {
  const classes = useStyles()
  const { allVendors, suppliers, refetch } = useAggregateVendors()

  const [summary, setSummary] = useState(null)
  const [summaryLoading, setSummaryLoading] = useState(true)

  // WVR sync state
  const [wvrSyncing, setWvrSyncing] = useState(false)
  const [wvrStatus, setWvrStatus] = useState(null)
  const [wvrPolling, setWvrPolling] = useState(false)
  const wvrPollRef = useRef(null)

  useEffect(() => {
    axios.get('/api/aggregate-dashboard/summary')
      .then((res) => setSummary(res.data))
      .catch((err) => console.error('[SuppliersTab] summary fetch error:', err))
      .finally(() => setSummaryLoading(false))
  }, [])

  const fetchWvrStatus = useCallback(() => {
    axios.get('/api/aggregate-dashboard/wvr/sync-status')
      .then((res) => {
        setWvrStatus(res.data)
        const isProcessing = res.data && res.data.status === 'processing'
        if (!isProcessing) {
          setWvrPolling(false)
          setWvrSyncing(false)
        }
      })
      .catch((err) => console.error('[SuppliersTab] wvrStatus fetch error:', err))
  }, [])

  // Fetch WVR status on mount
  useEffect(() => {
    fetchWvrStatus()
  }, [fetchWvrStatus])

  // Poll every 3 seconds while processing
  useEffect(() => {
    if (wvrPolling) {
      wvrPollRef.current = setInterval(fetchWvrStatus, 3000)
    } else {
      clearInterval(wvrPollRef.current)
    }
    return () => clearInterval(wvrPollRef.current)
  }, [wvrPolling, fetchWvrStatus])

  const handleWvrSync = () => {
    setWvrSyncing(true)
    axios.post('/api/aggregate-dashboard/wvr/sync')
      .then(() => {
        setWvrPolling(true)
      })
      .catch((err) => {
        console.error('[SuppliersTab] wvrSync error:', err)
        setWvrSyncing(false)
      })
  }

  const handleSyncAll = () => {
    setWvrSyncing(true)
    axios.post('/api/aggregate-dashboard/sync-all')
      .then(() => {
        setWvrPolling(true)
      })
      .catch((err) => {
        console.error('[SuppliersTab] syncAll error:', err)
        setWvrSyncing(false)
      })
  }

  // Configure Discovery dialog state
  const [configOpen, setConfigOpen] = useState(false)
  const [configVendor, setConfigVendor] = useState(null)
  const [configUrls, setConfigUrls] = useState([])
  const [configAutoSync, setConfigAutoSync] = useState(true)
  const [configSaving, setConfigSaving] = useState(false)
  const [configLoading, setConfigLoading] = useState(false)

  const handleOpenConfig = async (vendor) => {
    setConfigVendor(vendor)
    setConfigUrls([])
    setConfigAutoSync(true)
    setConfigOpen(true)
    setConfigLoading(true)
    try {
      const res = await axios.get(`/api/aggregate-dashboard/discovery-configs/${vendor._id}`)
      if (res.data && res.data.discoveryUrls) {
        setConfigUrls(res.data.discoveryUrls.map(u => ({ url: u.url, label: u.label || '', isActive: u.isActive !== false })))
        setConfigAutoSync(res.data.autoSync !== false)
      }
    } catch (err) {
      if (err.response?.status !== 404) console.error('[SuppliersTab] config fetch error:', err)
    } finally {
      setConfigLoading(false)
    }
  }

  const handleSaveConfig = async () => {
    if (!configVendor) return
    setConfigSaving(true)
    try {
      await axios.put('/api/aggregate-dashboard/discovery-configs', {
        vendorId: configVendor._id,
        discoveryUrls: configUrls.filter(u => u.url.trim()),
        autoSync: configAutoSync
      })
      setConfigOpen(false)
    } catch (err) {
      console.error('[SuppliersTab] config save error:', err)
    } finally {
      setConfigSaving(false)
    }
  }

  // Add Supplier dialog
  const [addOpen, setAddOpen] = useState(false)
  const [addMode, setAddMode] = useState('existing')
  const [addSaving, setAddSaving] = useState(false)
  const [addError, setAddError] = useState('')
  const [newVendorName, setNewVendorName] = useState('')
  const [newVendorPhone, setNewVendorPhone] = useState('')
  const [newVendorWebsite, setNewVendorWebsite] = useState('')
  const [selectedExistingId, setSelectedExistingId] = useState('')
  const [vendorSearch, setVendorSearch] = useState('')

  const nonAggregateVendors = allVendors.filter(
    (v) => !Array.isArray(v.capabilities) || !v.capabilities.some(c => c.type === 'aggregate_rock')
  )
  const filteredNonAggregate = nonAggregateVendors.filter(
    (v) => !vendorSearch || (v.name || '').toLowerCase().includes(vendorSearch.toLowerCase())
  )

  const handleAddSupplier = () => {
    setAddMode('existing')
    setAddError('')
    setNewVendorName('')
    setNewVendorPhone('')
    setNewVendorWebsite('')
    setSelectedExistingId('')
    setVendorSearch('')
    setAddOpen(true)
  }

  const handleAddSubmit = async () => {
    setAddSaving(true)
    setAddError('')
    try {
      if (addMode === 'existing') {
        if (!selectedExistingId) { setAddError('Select a vendor'); setAddSaving(false); return }
        const vendor = allVendors.find(v => v._id === selectedExistingId)
        const caps = Array.isArray(vendor?.capabilities) ? [...vendor.capabilities] : []
        caps.push({ type: 'aggregate_rock', isActive: true, scopes: [] })
        await axios.post('/api/vendors/updateVendor', { _id: selectedExistingId, capabilities: caps })
      } else {
        if (!newVendorName.trim()) { setAddError('Name is required'); setAddSaving(false); return }
        await axios.post('/api/company/createVendor', {
          name: newVendorName.trim(),
          phoneNumber: newVendorPhone.trim() || undefined,
          website: newVendorWebsite.trim() || undefined,
          active: true,
          capabilities: [{ type: 'aggregate_rock', isActive: true, scopes: [] }]
        })
      }
      setAddOpen(false)
      refetch()
    } catch (err) {
      setAddError(err.response?.data?.error || err.message)
    } finally {
      setAddSaving(false)
    }
  }

  // Discovery dialog state
  const [discoverOpen, setDiscoverOpen] = useState(false)
  const [discoverVendor, setDiscoverVendor] = useState(null)
  const [discoverUrl, setDiscoverUrl] = useState('')
  const [discoverLoading, setDiscoverLoading] = useState(false)
  const [discoverResults, setDiscoverResults] = useState(null)
  const [discoverError, setDiscoverError] = useState('')
  const [discoverImporting, setDiscoverImporting] = useState(false)

  const handleDiscoverPreview = async () => {
    if (!discoverUrl.trim()) return
    setDiscoverLoading(true)
    setDiscoverError('')
    setDiscoverResults(null)
    try {
      const res = await axios.get('/api/aggregate-dashboard/discover/preview', {
        params: { url: discoverUrl.trim() }
      })
      setDiscoverResults(res.data)
    } catch (err) {
      setDiscoverError(err.response?.data?.error || err.message)
    } finally {
      setDiscoverLoading(false)
    }
  }

  const handleDiscoverImport = async () => {
    if (!discoverVendor || !discoverUrl.trim()) return
    setDiscoverImporting(true)
    setDiscoverError('')
    try {
      const res = await axios.post('/api/aggregate-dashboard/discover', {
        vendorId: discoverVendor._id,
        url: discoverUrl.trim()
      })
      setDiscoverResults(res.data)
      setDiscoverImporting(false)
    } catch (err) {
      setDiscoverError(err.response?.data?.error || err.message)
      setDiscoverImporting(false)
    }
  }

  const wvrIsProcessing = wvrStatus && wvrStatus.status === 'processing'
  const wvrIsDone = wvrStatus && wvrStatus.status === 'completed'
  const wvrSyncDisabled = wvrSyncing || wvrIsProcessing

  return (
    <div className={classes.root}>
      {/* Sync All card */}
      <Paper variant="outlined" className={classes.wvrCard}>
        <div className={classes.wvrLeft}>
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>Catalog Sync</Typography>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={wvrSyncDisabled ? <CircularProgress size={14} color="inherit" /> : <SyncIcon />}
            onClick={handleSyncAll}
            disabled={wvrSyncDisabled}
          >
            {wvrIsProcessing ? 'Syncing...' : 'Sync All'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleWvrSync}
            disabled={wvrSyncDisabled}
          >
            WVR Only
          </Button>
        </div>
        <div className={classes.wvrRight}>
          {wvrIsProcessing && (
            <Typography variant="body2" color="textSecondary">Sync in progress…</Typography>
          )}
          {wvrIsDone && wvrStatus.result && (
            <>
              {wvrStatus.result.created != null && (
                <Chip label={`+${wvrStatus.result.created} Created`} size="small" className={classes.chipCreated} />
              )}
              {wvrStatus.result.updated != null && (
                <Chip label={`${wvrStatus.result.updated} Updated`} size="small" className={classes.chipUpdated} />
              )}
              {wvrStatus.result.unchanged != null && (
                <Chip label={`${wvrStatus.result.unchanged} Unchanged`} size="small" className={classes.chipUnchanged} />
              )}
              {wvrStatus.result.errors != null && wvrStatus.result.errors > 0 && (
                <Chip label={`${wvrStatus.result.errors} Errors`} size="small" className={classes.chipErrors} />
              )}
            </>
          )}
          {wvrStatus && wvrStatus.completedAt && (
            <Typography className={classes.wvrTimestamp}>
              Last sync: {new Date(wvrStatus.completedAt).toLocaleString()}
            </Typography>
          )}
          {wvrStatus && wvrStatus.status === 'none' && (
            <Typography className={classes.wvrTimestamp}>Never synced</Typography>
          )}
        </div>
      </Paper>

      {/* Stats row */}
      <div className={classes.statsRow}>
        <StatCard
          label="Suppliers"
          value={summaryLoading ? '—' : (summary ? summary.supplierCount : suppliers.length)}
          classes={classes}
        />
        <StatCard
          label="Locations"
          value={summaryLoading ? '—' : (summary ? summary.locationCount : '—')}
          classes={classes}
        />
        <StatCard
          label="Materials"
          value={summaryLoading ? '—' : (summary ? summary.materialCount : '—')}
          classes={classes}
        />
        <StatCard
          label="Offerings"
          value={summaryLoading ? '—' : (summary ? summary.offeringCount : '—')}
          classes={classes}
        />
        {summary && summary.unmappedOfferingCount > 0 && (
          <StatCard
            label="Unmapped"
            value={summary.unmappedOfferingCount}
            classes={classes}
            highlight
          />
        )}
      </div>

      {/* Table header */}
      <div className={classes.tableHeader}>
        <Typography variant="h6">Aggregate Suppliers</Typography>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddSupplier}
        >
          Add Supplier
        </Button>
      </div>

      {suppliers.length === 0 ? (
        <Paper variant="outlined">
          <div className={classes.emptyState}>
            <Typography variant="body1" color="textSecondary" gutterBottom>
              No aggregate suppliers configured yet.
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Configure vendors with the <strong>aggregate_rock</strong> capability to see them here.
            </Typography>
          </div>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Phone</strong></TableCell>
                <TableCell><strong>Website</strong></TableCell>
                <TableCell><strong>Locations</strong></TableCell>
                <TableCell><strong>Active</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {suppliers.map((vendor) => (
                <TableRow key={vendor._id} hover>
                  <TableCell>{vendor.name || '—'}</TableCell>
                  <TableCell>{vendor.phoneNumber || '—'}</TableCell>
                  <TableCell>
                    {vendor.website
                      ? <a href={vendor.website} target="_blank" rel="noopener noreferrer">{vendor.website}</a>
                      : '—'}
                  </TableCell>
                  <TableCell>—</TableCell>
                  <TableCell>
                    {vendor.active !== false
                      ? <Chip label="Active" size="small" className={classes.activeChip} />
                      : <Chip label="Inactive" size="small" className={classes.inactiveChip} />}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="text"
                      color="primary"
                      onClick={() => {
                        setDiscoverVendor(vendor)
                        setDiscoverUrl(vendor.website || '')
                        setDiscoverOpen(true)
                        setDiscoverResults(null)
                        setDiscoverError('')
                      }}
                    >
                      Discover
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<SettingsIcon style={{ fontSize: 16 }} />}
                      onClick={() => handleOpenConfig(vendor)}
                    >
                      Configure
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Discover Products Dialog */}
      <Dialog open={discoverOpen} onClose={() => !discoverLoading && !discoverImporting && setDiscoverOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Discover Products — {discoverVendor?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Enter a URL with product listings (e.g., /products or /tags/groundcover). We'll extract material/color names and add them as offerings.
          </Typography>
          <Box display="flex" style={{ gap: 8 }} mt={1} mb={2}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label="Product Page URL"
              value={discoverUrl}
              onChange={(e) => setDiscoverUrl(e.target.value)}
              placeholder="https://www.mdirock.com/tags/groundcover"
            />
            <Button
              variant="outlined"
              color="primary"
              onClick={handleDiscoverPreview}
              disabled={discoverLoading || !discoverUrl.trim()}
              style={{ whiteSpace: 'nowrap' }}
            >
              {discoverLoading ? <CircularProgress size={18} /> : 'Preview'}
            </Button>
          </Box>

          {discoverError && (
            <Typography variant="caption" style={{ color: '#c62828', display: 'block', marginBottom: 8 }}>
              {discoverError}
            </Typography>
          )}

          {discoverResults && (
            <Paper variant="outlined" style={{ maxHeight: 300, overflow: 'auto', padding: 8 }}>
              <Typography variant="subtitle2" gutterBottom>
                {discoverResults.profilesCreated !== undefined
                  ? `Imported: ${discoverResults.offeringsCreated} offerings, ${discoverResults.profilesCreated} new profiles, ${discoverResults.skipped} skipped`
                  : `Found ${discoverResults.count} products:`}
              </Typography>
              <List dense disablePadding>
                {(discoverResults.products || []).map((name, i) => (
                  <ListItem key={i} dense style={{ padding: '1px 0' }}>
                    <ListItemText primary={name} primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscoverOpen(false)} disabled={discoverImporting}>Close</Button>
          {discoverResults && discoverResults.profilesCreated === undefined && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleDiscoverImport}
              disabled={discoverImporting}
            >
              {discoverImporting ? <CircularProgress size={18} /> : `Import ${discoverResults.count} Products`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Add Supplier Dialog */}
      <Dialog open={addOpen} onClose={() => !addSaving && setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Aggregate Supplier</DialogTitle>
        <DialogContent>
          <Box display="flex" mb={2}>
            <Button
              variant={addMode === 'existing' ? 'contained' : 'outlined'}
              color="primary"
              size="small"
              onClick={() => setAddMode('existing')}
            >
              Existing Vendor
            </Button>
            <Button
              variant={addMode === 'new' ? 'contained' : 'outlined'}
              color="primary"
              size="small"
              onClick={() => setAddMode('new')}
              style={{ marginLeft: 8 }}
            >
              New Vendor
            </Button>
          </Box>
          <Divider style={{ marginBottom: 16 }} />

          {addMode === 'existing' ? (
            <Box>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Select an existing vendor to enable as an aggregate supplier:
              </Typography>
              <TextField
                fullWidth size="small" variant="outlined"
                placeholder="Search vendors..."
                value={vendorSearch}
                onChange={(e) => setVendorSearch(e.target.value)}
                style={{ marginBottom: 8 }}
              />
              <Paper variant="outlined" style={{ maxHeight: 250, overflow: 'auto' }}>
                <List dense disablePadding>
                  {filteredNonAggregate.length === 0 ? (
                    <ListItem><ListItemText secondary="No vendors found" /></ListItem>
                  ) : filteredNonAggregate.map((v) => (
                    <ListItem key={v._id} button selected={selectedExistingId === v._id} onClick={() => setSelectedExistingId(v._id)}>
                      <ListItemText primary={v.name} secondary={[v.phoneNumber, v.vendorType].filter(Boolean).join(' · ') || undefined} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" style={{ gap: 12 }}>
              <TextField label="Vendor Name *" value={newVendorName} onChange={(e) => setNewVendorName(e.target.value)} variant="outlined" size="small" fullWidth placeholder="e.g. MDI Rock" />
              <TextField label="Phone" value={newVendorPhone} onChange={(e) => setNewVendorPhone(e.target.value)} variant="outlined" size="small" fullWidth />
              <TextField label="Website" value={newVendorWebsite} onChange={(e) => setNewVendorWebsite(e.target.value)} variant="outlined" size="small" fullWidth placeholder="https://www.mdirock.com" />
            </Box>
          )}

          {addError && <Typography variant="caption" style={{ color: '#c62828', marginTop: 8, display: 'block' }}>{addError}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={addSaving}>Cancel</Button>
          <Button onClick={handleAddSubmit} color="primary" variant="contained" disabled={addSaving}>
            {addSaving ? <CircularProgress size={18} /> : 'Add Supplier'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Configure Discovery URLs Dialog */}
      <Dialog open={configOpen} onClose={() => !configSaving && setConfigOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Configure Discovery — {configVendor?.name}</DialogTitle>
        <DialogContent>
          {configLoading ? (
            <Box display="flex" justifyContent="center" p={3}><CircularProgress size={24} /></Box>
          ) : (
            <>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Add URLs for this supplier's product pages. These will be crawled during "Sync All" to discover materials.
              </Typography>

              {configUrls.map((entry, idx) => (
                <Box key={idx} display="flex" style={{ gap: 8 }} mt={1} alignItems="center">
                  <TextField
                    fullWidth size="small" variant="outlined"
                    label="URL" placeholder="https://rockprosusa.com/products"
                    value={entry.url}
                    onChange={(e) => {
                      const updated = [...configUrls]
                      updated[idx] = { ...updated[idx], url: e.target.value }
                      setConfigUrls(updated)
                    }}
                  />
                  <TextField
                    size="small" variant="outlined"
                    label="Label" placeholder="Products"
                    value={entry.label}
                    onChange={(e) => {
                      const updated = [...configUrls]
                      updated[idx] = { ...updated[idx], label: e.target.value }
                      setConfigUrls(updated)
                    }}
                    style={{ minWidth: 120 }}
                  />
                  <Button
                    size="small" color="secondary"
                    onClick={() => setConfigUrls(configUrls.filter((_, i) => i !== idx))}
                    style={{ minWidth: 32 }}
                  >
                    X
                  </Button>
                </Box>
              ))}

              <Box mt={1.5}>
                <Button
                  size="small" variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setConfigUrls([...configUrls, { url: configVendor?.website || '', label: '', isActive: true }])}
                >
                  Add URL
                </Button>
              </Box>

              <Box mt={2}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={configAutoSync}
                    onChange={(e) => setConfigAutoSync(e.target.checked)}
                  />
                  <Typography variant="body2">Include in "Sync All" runs</Typography>
                </label>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigOpen(false)} disabled={configSaving}>Cancel</Button>
          <Button onClick={handleSaveConfig} color="primary" variant="contained" disabled={configSaving || configLoading}>
            {configSaving ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

function StatCard ({ label, value, classes, highlight }) {
  return (
    <Paper variant="outlined" className={classes.statCard}>
      <Typography
        className={classes.statValue}
        style={highlight ? { color: '#e65100' } : undefined}
      >
        {value}
      </Typography>
      <Typography className={classes.statLabel}>{label}</Typography>
    </Paper>
  )
}
